import { useState, useEffect } from 'react';
import './App.css';
import CopyIcon from './copy-icon-white.svg'
import BackspaceIcon from './backspace.svg'


export const formatNumber = (numStr) => {
  if (isNaN(parseFloat(numStr))) return numStr
  const num = parseFloat(numStr)
  if (Number.isInteger(num)) {
    return num.toString()
  }
  const [integerPart, decimalPart] = numStr.split('.')
  if (decimalPart && decimalPart.length > 6) {
    const formatted = num.toFixed(11).replace(/\.?0+$/, '')
    if (decimalPart.length > 11) {
      return formatted + '...';
    }
    return formatted;
  }
  return numStr;
};

function App() {
  const [currentValue, setCurrentValue] = useState('0');
  const [previousValue, setPreviousValue] = useState('');
  const [operation, setOperation] = useState(null);
  const [overwrite, setOverwrite] = useState(true);
  const [showRoundOptions, setShowRoundOptions] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    const handleKeyDown = (event) => {
      const { key } = event;
      if (/[0-9]|\.|\+|\-|\*|\/|Enter|Escape|Backspace|%/.test(key)) {
        event.preventDefault();
      }
      if (/[0-9]/.test(key)) {
        addDigit(key);
      }
      if (key === '.') {
        addDigit('.');
      }
      if (key === '+') {
        chooseOperation('+');
      }
      if (key === '-') {
        chooseOperation('-');
      }
      if (key === '*') {
        chooseOperation('×');
      }
      if (key === '/') {
        chooseOperation('÷');
      }
      if (key === 'Enter' || key === '=') {
        equals();
      }
      if (key === 'Escape') {
        if (showRoundOptions) {
          setShowRoundOptions(false);
        } else {
          clear();
        }
      }
      if (key === 'Backspace') {
        deleteDigit();
      }
      if (key === '%') {
        convertToPercentage();
      }
      if ((event.ctrlKey || event.metaKey) && (key === 'c' || key === 'Insert')) {
        event.preventDefault();
        copyToClipboard();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentValue, previousValue, operation, overwrite, showRoundOptions]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentValue);
      setCopyStatus('Copiado!');
      setTimeout(() => {
        setCopyStatus('');
      }, 2000);
    } catch (err) {
      console.error('Copy failed: ', err);
      setCopyStatus('Copy failed');
      setTimeout(() => {
        setCopyStatus('');
      }, 2000);
    }
  };



  const addDigit = (digit) => {
    if (currentValue === '0' || overwrite) {
      setCurrentValue(digit);
      setOverwrite(false);
    } else {
      if (digit === '.' && currentValue.includes('.')) return;
      setCurrentValue(`${currentValue}${digit}`);
    }
  };

  const chooseOperation = (nextOperation) => {
    if (currentValue === '0' && previousValue && operation) {
      setOperation(nextOperation);
      return;
    }

    if (previousValue && operation && !overwrite) {
      const result = calculate();
      setCurrentValue(result);
      setPreviousValue(result);
    } else {
      setPreviousValue(currentValue);
    }

    setOverwrite(true);
    setOperation(nextOperation);
  };

  const calculate = () => {
    if (!operation || !previousValue || !currentValue) return currentValue;

    const prev = parseFloat(previousValue);
    const current = parseFloat(currentValue);
    let result;

    switch (operation) {
      case '+':
        result = prev + current;
        break;
      case '-':
        result = prev - current;
        break;
      case '×':
        result = prev * current;
        break;
      case '÷':
        result = prev / current;
        break;
      default:
        return currentValue;
    }

    return formatNumber(result.toString());
  };

  const equals = () => {
    const result = calculate();
    setCurrentValue(result);
    setPreviousValue('');
    setOperation(null);
    setOverwrite(true);
  };

  const clear = () => {
    setCurrentValue('0');
    setPreviousValue('');
    setOperation(null);
    setOverwrite(true);
    setShowRoundOptions(false);
    setCopyStatus('');
  };

  const deleteDigit = () => {
    if (overwrite || currentValue.length === 1) {
      setCurrentValue('0');
      setOverwrite(true);
    } else {
      setCurrentValue(currentValue.slice(0, -1));
    }
  };

  const convertToPercentage = () => {
    setCurrentValue((parseFloat(currentValue) / 100).toString());
  };

  const roundNumber = (decimalPlaces) => {
    const num = parseFloat(currentValue);
    if (isNaN(num)) return;

    const multiplier = Math.pow(10, decimalPlaces);
    const rounded = Math.round(num * multiplier) / multiplier;

    setCurrentValue(rounded.toFixed(decimalPlaces));
    setShowRoundOptions(false);
  };

  const toggleRoundOptions = () => {
    setShowRoundOptions(!showRoundOptions);
  };

  return (
    <div className="calculator">
      <div className="display-container">
        <div className="display">
          <div className="previous-operand">
            {previousValue} {operation}
          </div>
          <div className="current-operand">
            {currentValue}
          </div>
        </div>
        <button
          className="copy-btn"
          onClick={copyToClipboard}
          title="Copy result (Ctrl+C)"
        >
          <img src={CopyIcon} />
        </button>
        {copyStatus && <div className="copy-status">{copyStatus}</div>}
      </div>

      <div className="buttons">
        <button className="special" onClick={clear} title="Clear (Esc)">C</button>
        <button className="special" onClick={deleteDigit} title="Delete (Backspace)">
          <img src={BackspaceIcon} />
        </button>
        <button onClick={convertToPercentage} title="Percentage (%)">%</button>
        <button className="operation" onClick={toggleRoundOptions} title="Round number">Round</button>

        <button onClick={() => addDigit('7')}>7</button>
        <button onClick={() => addDigit('8')}>8</button>
        <button onClick={() => addDigit('9')}>9</button>
        <button className="operation" onClick={() => chooseOperation('÷')} title="Divide (/)">÷</button>

        <button onClick={() => addDigit('4')}>4</button>
        <button onClick={() => addDigit('5')}>5</button>
        <button onClick={() => addDigit('6')}>6</button>
        <button className="operation" onClick={() => chooseOperation('×')} title="Multiply (*)">×</button>

        <button onClick={() => addDigit('1')}>1</button>
        <button onClick={() => addDigit('2')}>2</button>
        <button onClick={() => addDigit('3')}>3</button>
        <button className="operation" onClick={() => chooseOperation('-')} title="Subtract (-)">-</button>

        <button onClick={() => addDigit('0')}>0</button>
        <button onClick={() => addDigit('.')} title="Decimal Point (.)">.</button>
        <button className="equals" onClick={equals} title="Equals (Enter or =)">=</button>
        <button className="operation" onClick={() => chooseOperation('+')} title="Add (+)">+</button>
      </div>

      {showRoundOptions && (
        <div className="modal-overlay" onClick={() => setShowRoundOptions(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Round number</h3>
              <button className="close-btn" onClick={() => setShowRoundOptions(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Select how many decimal places you want:</p>
              <div className="decimal-buttons">
                {[0, 1, 2, 3, 4, 5].map((decimal) => (
                  <button
                    key={decimal}
                    className="decimal-btn"
                    onClick={() => roundNumber(decimal)}
                  >
                    {decimal} {decimal === 1 ? 'place' : 'places'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;